// #564 T3: „Frage an die Verwaltung“ – Betreff und Text, daraus wird ein Ticket „Anfrage“ (Mieter und Eigentümer).
// Am Handy als Blatt von unten, sonst mittig; Esc schließt, solange nicht gesendet wird. Es wird nichts verschickt.
import { useEffect, useState } from 'react';
import { CaretRight, ChatCircleText, Check, CircleNotch, PaperPlaneTilt, X } from '@phosphor-icons/react';
import { IconChip } from './bausteine';
import { MIN_ANFRAGE_TEXT, pruefeAnfrage } from '../../lib/portal/nachrichtenApi';

export default function AnfrageSheet({ unterzeile, senden, onSchliessen, onGesendet, onZuListe, listeName }: {
  unterzeile: string;
  senden: (betreff: string, text: string) => Promise<{ ticket_nummer: string }>;
  onSchliessen: () => void;
  onGesendet: () => void;
  onZuListe: () => void;
  listeName: string;
}) {
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [versucht, setVersucht] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);
  const p = pruefeAnfrage(betreff, text);

  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sendet) onSchliessen(); };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [sendet, onSchliessen]);

  const absenden = async () => {
    setVersucht(true);
    if (!p.ok || sendet) return;
    setSendet(true); setFehler(null);
    try { const r = await senden(betreff, text); setFertig(r.ticket_nummer); onGesendet(); } catch (e) { setFehler((e as Error).message); } finally { setSendet(false); }
  };
  const feld = (fehlt: boolean) => ({ width: '100%', boxSizing: 'border-box' as const, borderRadius: 14, padding: '11px 13px', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
    color: 'var(--p-w)', background: 'var(--p-w05)', border: 'none', boxShadow: fehlt ? 'var(--p-fehler-ring)' : 'inset 0 0 0 1px var(--p-w08)' });

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="anfrage-titel" className="mp-sheet-huelle" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'center' }}>
      <div onClick={() => !sendet && onSchliessen()} aria-hidden
        style={{ position: 'absolute', inset: 0, background: 'var(--p-sheet-schleier)', WebkitBackdropFilter: 'blur(18px) saturate(120%)', backdropFilter: 'blur(18px) saturate(120%)' }} />
      <div className="mp-sheet p-blatt" style={{ position: 'relative', width: '100%', maxWidth: 600, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--p-sheet)', boxShadow: 'var(--p-sheet-sh)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px 18px' }}>
          <IconChip farbe="gemein" icon={ChatCircleText} groesse={40} radius={12} />
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span id="anfrage-titel" style={{ fontSize: 18, fontWeight: 800 }}>Frage an die Verwaltung</span>
            <span className="p-kuerzen" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w50)' }}>{unterzeile}</span>
          </span>
          <button type="button" className="p-rund" onClick={onSchliessen} disabled={sendet} data-tip="Schließen ohne zu senden" aria-label="Schließen ohne zu senden" style={{ width: 36, height: 36, color: 'var(--p-w70)' }}>
            <X size={16} weight="bold" />
          </button>
        </div>
        {fertig ? (
          <div role="status" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '26px 24px 24px', textAlign: 'center' }}>
            <span className="p-farbe-konto p-chip" style={{ width: 70, height: 70, borderRadius: 35 }}><Check size={34} weight="bold" /></span>
            <span style={{ fontSize: 21, fontWeight: 800 }}>Anfrage {fertig} ist eingegangen</span>
            <span style={{ maxWidth: 420, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, color: 'var(--p-w60)' }}>Die Verwaltung antwortet Ihnen hier im Portal. Sie finden die Anfrage unter „{listeName}“.</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button type="button" className="p-knopf" onClick={onSchliessen} style={{ height: 42, border: 'none', background: 'var(--p-w08)' }}>Fertig</button>
              <button type="button" className="p-knopf p-knopf-gold" onClick={onZuListe} data-tip={`Zu „${listeName}“`} style={{ height: 42 }}>Zu „{listeName}“ <CaretRight size={14} weight="bold" /></button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '6px 18px 18px', overflowY: 'auto' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="mp-label" style={{ color: versucht && !p.betreff ? 'var(--p-fehler-text)' : undefined }}>Betreff *</span>
                <input value={betreff} onChange={(e) => setBetreff(e.target.value)} maxLength={120} placeholder="z. B. Nebenkostenabrechnung 2025" style={feld(versucht && !p.betreff)} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="mp-label" style={{ color: versucht && !p.text ? 'var(--p-fehler-text)' : undefined }}>Ihre Frage *</span>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} maxLength={4000} placeholder="Worum geht es?" style={{ ...feld(versucht && !p.text), resize: 'vertical' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: versucht && !p.text ? 'var(--p-fehler-text)' : 'var(--p-w45)' }}>
                  {versucht && !p.text ? `Bitte beschreiben Sie Ihr Anliegen in ein paar Worten (mindestens ${MIN_ANFRAGE_TEXT} Zeichen).` : 'Für Schäden bitte „Schaden melden“ nutzen, dann kann direkt ein Handwerker kommen.'}
                </span>
              </label>
              {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-fehler-text)' }}>{fehler}</span>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', borderTop: '0.5px solid var(--p-w08)', background: 'var(--p-sheet-fuss)', flexWrap: 'wrap' }}>
              <button type="button" className="p-knopf" onClick={onSchliessen} disabled={sendet} data-tip="Abbrechen ohne zu senden" style={{ height: 42, border: 'none', background: 'var(--p-w08)' }}>Abbrechen</button>
              <button type="button" className="p-knopf p-knopf-gold" onClick={() => void absenden()} disabled={sendet} data-tip="Anfrage an die Verwaltung senden" style={{ height: 42 }}>
                {sendet ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <PaperPlaneTilt size={16} weight="bold" />}
                {sendet ? 'Wird gesendet …' : 'Anfrage senden'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
